import { computeBookingTotal } from "./bookingExtras";
export type CostBasis = "flat" | "per_person";

export interface CostLine {
  cost_basis: CostBasis | string;
  unit_amount: number | string | null;
}

export interface IncomeBooking {
  status?: string | null;
  payment_status?: string | null;
  is_test?: boolean | null;
  payment_method?: string | null;
  yoco_checkout_id?: string | null;
  amount: number;
}

export const BANK_FEE_RATE = 0.035;
const CARD_METHODS = ["card_machine", "payment_link", "pay_now"];

export function costLineTotal(line: CostLine, headcount: number): number {
  const unit = Number(line.unit_amount || 0);
  return line.cost_basis === "per_person" ? unit * Math.max(0, headcount) : unit;
}

/** Paid, non-cancelled, non-test bookings count as session income. */
export function isCountedIncome(b: IncomeBooking): boolean {
  return b.status !== "cancelled" && !b.is_test && b.payment_status === "paid";
}

/** Estimated view: also counts invoiced / unpaid bookings that aren't cancelled or tests. */
export function isEstimatedIncome(b: IncomeBooking): boolean {
  return b.status !== "cancelled" && !b.is_test;
}

/** All non-cancelled lines of a booking that belong to the same session (name + date). */
export function sessionLines<T extends { booking_id: string; experience_name?: string | null; experience_date?: string | null; status?: string | null }>(
  items: T[], b: { id: string; experience_name?: string | null; experience_date?: string | null },
): T[] {
  return items.filter((i) => i.booking_id === b.id && i.status !== "cancelled"
    && (!b.experience_name || i.experience_name === b.experience_name)
    && (!b.experience_date || i.experience_date === b.experience_date));
}

/** Net income for a booking's session lines: sum of lines, minus agent commission unless already deducted, plus extras. */
export function sessionNetIncome({ lines, commissionRate, commissionAlreadyDeducted, extras = [] }: {
  lines: Array<{ subtotal?: number | string | null }>; commissionRate: number; commissionAlreadyDeducted?: boolean;
  extras?: Array<{ amount: number; basis: "flat" | "per_person"; quantity: number; include_in_commission?: boolean; status?: string }>;
}): number {
  const tours = lines.reduce((s, i) => s + Number(i.subtotal || 0), 0);
  return computeBookingTotal({ toursSubtotal: tours, commissionRate, commissionAlreadyDeducted, extras: extras.map((e) => ({ description: "x", notify_supplier: false, ...e })) }).total;
}

/** Paid by card: online via Yoco, payment link, or internal card machine. */
export function isCardPayment(b: IncomeBooking): boolean {
  return !!b.yoco_checkout_id || CARD_METHODS.includes(b.payment_method || "");
}

export function bookingBankFee(b: IncomeBooking): number {
  return isCountedIncome(b) && isCardPayment(b) ? Number(b.amount || 0) * BANK_FEE_RATE : 0;
}

export function bankFeeTotal(bookings: IncomeBooking[]): number {
  return bookings.reduce((s, b) => s + bookingBankFee(b), 0);
}

/** Override (if set) replaces the automatic 3.5% figure. */
export function effectiveBankFee(bookings: IncomeBooking[], override?: number | null): number {
  return override != null ? Number(override) : bankFeeTotal(bookings);
}

export function calculateSessionPnl(
  bookings: IncomeBooking[],
  costs: CostLine[],
  headcount: number,
  estimated = false,
) {
  const income = bookings.filter(estimated ? isEstimatedIncome : isCountedIncome).reduce((s, b) => s + Number(b.amount || 0), 0);
  const totalCosts = costs.reduce((s, c) => s + costLineTotal(c, headcount), 0);
  const profit = income - totalCosts;
  const margin = income ? (profit / income) * 100 : 0;
  return { income, totalCosts, profit, margin };
}

/**
 * Client-facing income for one lead activity: its cost share of the accepted
 * quote total (markup + fees spread in proportion to cost).
 */
export function leadActivityIncome(itemCost: number, allItemsCost: number, quoteTotal: number): number {
  if (!allItemsCost || !quoteTotal) return 0;
  return (itemCost / allItemsCost) * quoteTotal;
}

/** Paid bookings whose lines all sit in this session count what was actually received. */
export function sessionIncomeAmount({ calculated, paymentStatus, amountPaid, wholeBookingInSession }: {
  calculated: number; paymentStatus?: string | null; amountPaid?: number | string | null; wholeBookingInSession: boolean;
}): { amount: number; paidDiffers: boolean } {
  const paid = Number(amountPaid || 0);
  if (paymentStatus === "paid" && paid > 0 && wholeBookingInSession && Math.abs(paid - calculated) >= 0.01) return { amount: paid, paidDiffers: true };
  return { amount: calculated, paidDiffers: false };
}

/**
 * A booking saved below its gross (lines + extras) carries a consultant discount;
 * that saved total is the final net figure, so no commission is taken off it.
 */
export function discountedNetIncome({ calculatedGross, savedTotal, wholeBookingInSession }: {
  calculatedGross: number; savedTotal?: number | string | null; wholeBookingInSession: boolean;
}): number | null {
  const saved = Number(savedTotal || 0);
  if (!wholeBookingInSession || saved <= 0) return null;
  return saved < calculatedGross - 0.5 ? Math.round(saved * 100) / 100 : null;
}
